import React, { useEffect, useState } from 'react'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { Tooltip } from '@astryxdesign/core/Tooltip'
import { Token } from '@astryxdesign/core/Token'
import { Tokenizer } from '@astryxdesign/core/Tokenizer'
import { GENRE_ITEMS, GENRE_SEARCH_SOURCE, type GenreItem } from '../lib/genres'
import * as stylex from '@stylexjs/stylex'

interface ToneInfo {
  name: string
  description: string
  detailedDescription: string
}

interface PerspectiveInfo {
  name: string
  description: string
  detailedDescription: string
}

interface SettingInfo {
  name: string
  description: string
  detailedDescription: string
}

const POPULAR_GENRE_NAMES = ['Fantasy', 'Science Fiction', 'Romance', 'Mystery', 'Thriller']
const POPULAR_GENRE_ITEMS: GenreItem[] = POPULAR_GENRE_NAMES.map(
  (name) => GENRE_ITEMS.find((item) => item.label === name)!
)

type OnboardingStep = 'genre' | 'tone' | 'character' | 'perspective' | 'setting' | 'prompt'

interface OnboardingDraft {
  step: OnboardingStep
  genreLabels: string[]
  selectedTones: string[]
  toneText: string
  characterText: string
  selectedPerspective: string
  perspectiveText: string
  selectedSetting: string
  settingText: string
  promptText: string
}

const EMPTY_DRAFT: OnboardingDraft = {
  step: 'genre',
  genreLabels: [],
  selectedTones: [],
  toneText: '',
  characterText: '',
  selectedPerspective: '',
  perspectiveText: '',
  selectedSetting: '',
  settingText: '',
  promptText: ''
}

const getOnboardingStorageKey = (storyId?: string) => `onboarding-draft:${storyId ?? 'default'}`

const loadOnboardingDraft = (storyId?: string): OnboardingDraft => {
  try {
    const raw = localStorage.getItem(getOnboardingStorageKey(storyId))
    if (!raw) return EMPTY_DRAFT
    const parsed = JSON.parse(raw)
    return { ...EMPTY_DRAFT, ...parsed }
  } catch {
    return EMPTY_DRAFT
  }
}

const clearOnboardingDraft = (storyId?: string) => {
  try {
    localStorage.removeItem(getOnboardingStorageKey(storyId))
  } catch {
    // localStorage unavailable (private browsing, quota) - nothing to clean up
  }
}

const TONES: ToneInfo[] = [
  {
    name: 'Dark & Gritty',
    description: 'Bleak, realistic, morally grey, and intense.',
    detailedDescription:
      'Bleak, realistic, morally grey, and intense. A gritty world where choices have consequences, characters struggle against harsh realities, and the line between good and evil is blurred.'
  },
  {
    name: 'Lighthearted & Humorous',
    description: 'Funny, satirical, and designed to make the reader smile.',
    detailedDescription:
      'Funny, satirical, and designed to make the reader smile. Filled with witty banter, amusing situations, and playful interactions that keep the mood bright and enjoyable.'
  },
  {
    name: 'Suspenseful & Tense',
    description: 'Fast-paced, full of anticipation, danger, and suspense.',
    detailedDescription:
      'Fast-paced, full of anticipation, danger, and suspense. Every scene builds suspense, keeping the reader on edge and wondering what will happen next.'
  },
  {
    name: 'Romantic & Sensual',
    description: 'Focuses on love, passion, emotional connection, and intimacy.',
    detailedDescription:
      'Focuses on love, passion, emotional connection, and intimacy. Emphasizes the deep bond, growing attraction, and personal connection between characters as they navigate their feelings.'
  },
  {
    name: 'Melancholic & Sad',
    description: 'Reflective, bittersweet, touching, and emotionally heavy.',
    detailedDescription:
      'Reflective, bittersweet, touching, and emotionally heavy. Explores themes of loss, longing, and grief, capturing a sense of sorrow that touches the heart.'
  },
  {
    name: 'Whimsical & Playful',
    description: 'Imaginative, light, magical, and fun.',
    detailedDescription:
      'Imaginative, light, magical, and fun. Filled with wonder, curiosity, and charming elements that evoke a sense of childhood magic and exploration.'
  },
  {
    name: 'Mysterious & Intriguing',
    description: 'Focuses on puzzles, secrets, and suspenseful investigations.',
    detailedDescription:
      'Focuses on puzzles, secrets, and suspenseful investigations. Keeps readers guessing with hidden motives, clues, and complex conspiracies waiting to be slowly uncovered.'
  },
  {
    name: 'Epic & Grand',
    description: 'High stakes, grand scale, legendary characters, and sweeping conflicts.',
    detailedDescription:
      'High stakes, grand scale, legendary characters, and sweeping conflicts. Explores monumental struggles, ancient lore, and destiny on a massive scale.'
  },
  {
    name: 'Philosophical & Reflective',
    description: 'Thought-provoking, examining life, morality, and deep themes.',
    detailedDescription:
      'Thought-provoking, examining life, morality, and deep themes. Delves into deep questions about life, morality, technology, and humanity, leaving readers with ideas to ponder.'
  },
  {
    name: 'Hopeful & Uplifting',
    description: 'Optimistic, inspiring, focusing on triumph and positive resolution.',
    detailedDescription:
      'Optimistic, inspiring, focusing on triumph and positive resolution. Showcases characters overcoming adversity to find light, joy, and a positive future.'
  },
  {
    name: 'Action-packed',
    description: 'High-octane, fast, full of fights, pursuits, and physical energy.',
    detailedDescription:
      'High-octane, fast, full of fights, pursuits, and physical energy. Emphasizes exciting sequences, physical conflicts, and high-stakes danger.'
  },
  {
    name: 'Eerie & Spooky',
    description: 'Creepy, atmospheric, dealing with ghosts or paranormal dread.',
    detailedDescription:
      'Creepy, atmospheric, dealing with ghosts or paranormal dread. Focuses on the unknown, supernatural mysteries, and ghostly elements that chill the spine.'
  },
  {
    name: 'Nostalgic & Bittersweet',
    description: 'Reminiscent, looking back on the past with mixed joy and sorrow.',
    detailedDescription:
      'Reminiscent, looking back on the past with mixed joy and sorrow. A warm, reflective look at the past, blending the sweetness of happy memories with the quiet sadness of time gone by.'
  },
  {
    name: 'Cozy & Comforting',
    description: 'Low stakes, warm, focusing on community and daily life comfort.',
    detailedDescription:
      'Low stakes, warm, focusing on community and daily life comfort. A warm, comforting story centered on community, simple pleasures, and safe, inviting environments.'
  },
  {
    name: 'Satirical & Sarcastic',
    description: 'Mocking conventions, witty social commentary, and dry humor.',
    detailedDescription:
      'Mocking conventions, witty social commentary, and dry humor. A sharp, clever commentary using irony, sarcasm, and dry wit to mock genre tropes, societal expectations, and human folly.'
  }
]

const PERSPECTIVES: PerspectiveInfo[] = [
  {
    name: 'First Person',
    description: "The story is told using 'I' or 'we' from a character's point of view.",
    detailedDescription:
      "Written from the main character's point of view, using 'I' or 'we'. The reader experiences the story through their eyes, thoughts, and emotions directly."
  },
  {
    name: 'Third Person Limited',
    description: "The story follows a single character using 'he', 'she', or 'they'.",
    detailedDescription:
      "Written from an external narrator's point of view, using 'he', 'she', or 'they', but closely following a single character's perspective, thoughts, and feelings."
  },
  {
    name: 'Third Person Omniscient',
    description: "An all-knowing narrator describes all characters' thoughts and events.",
    detailedDescription:
      "Written from an all-knowing narrator's point of view, using 'he', 'she', or 'they'. The narrator has access to all characters' thoughts, feelings, and events across the entire story world."
  },
  {
    name: 'Second Person',
    description: "The story addresses the reader directly as 'you'.",
    detailedDescription:
      "Written from the perspective of addressing the reader directly as 'you', making the reader the protagonist of the story."
  },
  {
    name: 'Multiple POV',
    description: 'The story alternates points of view between different characters.',
    detailedDescription:
      "Written from multiple alternating points of view (first or third person limited), giving the reader insight into different characters' experiences across different chapters or scenes."
  }
]

const SETTINGS: SettingInfo[] = [
  {
    name: 'Cozy NYC',
    description: "Cozy NYC (2020s, reminiscent of You've Got Mail)",
    detailedDescription:
      'Set in a warm, romanticized, and nostalgic version of 2020s New York City. The air is crisp, and dry orange leaves tumble along the sidewalks of Greenwich Village. Characters grab hot apple cider from neighborhood cafes, browse dusty shelves at independent corner bookstores, and navigate their careers and relationships through soft screen glows. It is a world of serendipitous encounters, quiet brownstone stoops, and the gentle, optimistic hum of modern urban connection.'
  },
  {
    name: 'Victorian London',
    description: 'Victorian London (Foggy, gothic, gaslit streets)',
    detailedDescription:
      'Set in late-19th century gaslit London, shrouded in a heavy, coal-scented fog that clings to cobblestone streets. Grand brick estates stand in stark contrast to crowded dockside alleyways, where carriage wheels click rhythmic patterns in the night. Secrets whisper behind velvet curtains in aristocratic parlors, while gas lamps flicker weakly against the encroaching shadows. It is a world of rigid social etiquette, gothic mysteries, and dark industrial progress.'
  },
  {
    name: 'Neon Cyberpunk',
    description: 'Neon Cyberpunk Metropolis (High-tech, rain-slicked)',
    detailedDescription:
      'Set in a sprawling futuristic metropolis where towering skyscrapers block out the sky, and slick, rain-drenched streets reflect endless neon advertisements. Drones buzz between holographic signs while citizens navigate crowded, multi-level street markets selling street food and black-market cyberware. Rain falls constantly, washing over shiny metal and plastic surfaces. It is a high-tech, low-life world where corporate greed rules, and hackers and outcasts carve out a survival in the dark back alleys.'
  },
  {
    name: 'Cozy Fantasy Town',
    description: 'Cozy Fantasy Village (Quaint, magical, tranquil)',
    detailedDescription:
      'Set in a tranquil, quaint village nestled between rolling green hills and a whispering forest. Stone cottages feature smoking chimneys, flower boxes, and thatched roofs, while gentle magic flows through daily chores—from self-sweeping brooms to glowing lantern-lilies. Friendly mythical beasts help harvest fields, and neighbors gather at the local tavern for warm stews and soft bardic music. It is a slow-paced, comforting world of community, small wonders, and peaceful exploration.'
  },
  {
    name: 'Space Colony',
    description: 'Desert Outpost or Space Colony (Frontier, isolated)',
    detailedDescription:
      'Set in a remote desert outpost on the frontier of a barren, red-sanded planet, or within the metallic hulls of an isolated space station orbiting a giant, ringed gas planet. The constant low hum of life-support generators and air purifiers is a reminder of the vacuum outside, where stars burn silently in a dark void. Scavengers, miners, and crew members barter for water, share stories of home, and repair weathered machinery. It is a harsh but hopeful world of isolation, engineering survival, and empty space frontiers.'
  }
]

interface StoryOnboardingProps {
  storyId?: string
  onExit?: () => void
  onComplete: (
    genres: string[],
    tones: string[],
    toneText: string,
    characterText: string,
    perspective: string,
    perspectiveText: string,
    setting: string,
    settingText: string,
    promptText: string
  ) => Promise<boolean>
}

const styles = stylex.create({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
    padding: '24px',
    overflowY: 'auto',
    maxHeight: '100vh',
    position: 'relative',
    '@media (min-width: 768px)': {
      padding: '48px'
    }
  },
  container: {
    maxWidth: '1024px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: '32px'
  },
  heading: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: 'var(--text-heading)',
    marginTop: '8px'
  },
  headerGroup: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px'
  },
  genreNoticeText: {
    fontSize: '14px',
    color: 'var(--text-muted)'
  },
  genreCaption: {
    fontSize: '13px',
    color: 'var(--text-muted)',
    marginTop: '4px'
  },
  genreDropdownItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '4px',
    width: '100%'
  },
  genreDropdownDescription: {
    fontSize: '13px',
    color: 'var(--text-muted)',
    textAlign: 'left'
  },
  popularGenresRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginTop: '20px'
  },
  popularGenresLabel: {
    fontSize: '13px',
    fontWeight: '600',
    color: 'var(--text-muted)',
    whiteSpace: 'nowrap'
  },
  buttonsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    alignItems: 'center',
    width: '100%',
    marginTop: '28px'
  },
  genreButtonsContainer: {
    marginTop: '4px'
  },
  row: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'center',
    flexWrap: 'wrap'
  },
  actionContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '50px',
    width: '100%',
    marginTop: '24px'
  },
  skipLink: {
    background: 'none',
    border: 'none',
    color: 'var(--text-muted)',
    cursor: 'pointer',
    fontSize: '13px',
    textDecoration: 'none',
    outline: 'none',
    ':hover': {
      textDecoration: 'underline',
      color: 'var(--text-main)'
    }
  },
  promptWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '24px',
    width: '100%',
    maxWidth: '640px',
    marginTop: '28px'
  },
  suggestionsContainer: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
    flexWrap: 'wrap',
    marginTop: '8px'
  },
  suggestionCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    borderRadius: '9999px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'var(--color-border)',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    cursor: 'pointer',
    fontSize: '13px',
    color: 'var(--text-main)',
    transition: 'background-color 0.2s ease, border-color 0.2s ease',
    ':hover': {
      backgroundColor: 'var(--hover-bg)',
      borderColor: 'var(--brand-color)'
    }
  },
  scratchLink: {
    background: 'none',
    border: 'none',
    color: 'var(--text-muted)',
    cursor: 'pointer',
    fontSize: '13px',
    textDecoration: 'none',
    marginTop: '24px',
    outline: 'none',
    ':hover': {
      textDecoration: 'underline',
      color: 'var(--text-main)'
    }
  },
  genreButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px 20px',
    borderRadius: '9999px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'var(--color-border)',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    cursor: 'pointer',
    fontSize: '13px',
    color: 'var(--text-main)',
    transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease',
    outline: 'none',
    userSelect: 'none',
    ':hover': {
      backgroundColor: 'var(--hover-bg)',
      borderColor: 'var(--brand-color)'
    }
  },
  genreButtonSelected: {
    borderColor: 'var(--brand-color)',
    backgroundColor: 'var(--active-bg)',
    color: 'var(--brand-color)',
    fontWeight: '500',
    ':hover': {
      backgroundColor: 'var(--active-bg)',
      borderColor: 'var(--brand-color)'
    }
  },
  genreButtonDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
    ':hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.03)',
      borderColor: 'var(--color-border)'
    }
  },
  toneTextareaContainer: {
    width: '100%',
    maxWidth: '640px',
    marginTop: '8px',
    borderRadius: '32px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'var(--color-border)',
    backgroundColor: 'var(--color-background-surface)',
    padding: '16px 28px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)',
    display: 'flex',
    flexDirection: 'column'
  },
  toneTextarea: {
    width: '100%',
    height: '72px',
    backgroundColor: 'transparent',
    borderStyle: 'none',
    borderWidth: 0,
    outline: 'none',
    resize: 'none',
    color: 'var(--text-main)',
    fontSize: '15px',
    lineHeight: '1.5',
    fontFamily: 'inherit',
    padding: 0,
    margin: 0,
    ':focus': {
      outline: 'none',
      boxShadow: 'none'
    },
    '::placeholder': {
      color: 'var(--text-muted)'
    }
  },
  characterTextareaContainer: {
    width: '100%',
    maxWidth: '640px',
    marginTop: '8px',
    borderRadius: '32px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'var(--color-border)',
    backgroundColor: 'var(--color-background-surface)',
    padding: '20px 28px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)',
    display: 'flex',
    flexDirection: 'column'
  },
  characterTextarea: {
    width: '100%',
    height: '180px',
    backgroundColor: 'transparent',
    borderStyle: 'none',
    borderWidth: 0,
    outline: 'none',
    resize: 'none',
    color: 'var(--text-main)',
    fontSize: '15px',
    lineHeight: '1.5',
    fontFamily: 'inherit',
    padding: 0,
    margin: 0,
    ':focus': {
      outline: 'none',
      boxShadow: 'none'
    },
    '::placeholder': {
      color: 'var(--text-muted)'
    }
  },
  characterGuideText: {
    fontSize: '14px',
    lineHeight: '1.6',
    color: 'var(--text-muted)',
    maxWidth: '600px',
    marginTop: '4px',
    textAlign: 'center'
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: 'hsl(var(--background))',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'var(--color-border)',
    color: 'var(--text-main)',
    cursor: 'pointer',
    outline: 'none',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
    transition: 'background-color 0.2s ease, transform 0.1s ease',
    ':hover': {
      backgroundColor: 'var(--hover-bg)'
    },
    ':active': {
      transform: 'scale(0.95)'
    }
  },
  forwardButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: '#1b62f3',
    color: '#ffffff',
    border: 'none',
    cursor: 'pointer',
    outline: 'none',
    boxShadow: '0 2px 8px rgba(27, 98, 243, 0.25)',
    transition: 'background-color 0.2s ease, transform 0.1s ease',
    ':hover': {
      backgroundColor: '#0f4fd0'
    },
    ':active': {
      transform: 'scale(0.95)'
    }
  },
  forwardButtonDisabled: {
    backgroundColor: 'var(--color-border)',
    color: 'var(--text-muted)',
    cursor: 'not-allowed',
    boxShadow: 'none',
    opacity: 0.5,
    ':hover': {
      backgroundColor: 'var(--color-border)'
    }
  }
})

export default function StoryOnboarding({
  storyId,
  onExit,
  onComplete
}: StoryOnboardingProps): React.JSX.Element {
  // Read localStorage once, on mount, via a lazy state initializer rather than a ref filled in
  // during render. Both compute the draft exactly once, but only this one is a pure render: the
  // initializer runs inside React's state machinery, so a re-rendered or replayed render never
  // re-reads storage. Deliberately never re-read on a storyId change -- the seeded useState calls
  // below wouldn't pick up a new draft anyway, so remounting is what changes stories.
  const [draft] = useState<OnboardingDraft>(() => loadOnboardingDraft(storyId))

  const [step, setStep] = useState<OnboardingStep>(draft.step)
  const [selectedGenreItems, setSelectedGenreItems] = useState<GenreItem[]>(() =>
    draft.genreLabels
      .map((label) => GENRE_ITEMS.find((item) => item.label === label))
      .filter((item): item is GenreItem => item != null)
  )
  const selectedGenres = selectedGenreItems.map((item) => item.label)
  const [selectedTones, setSelectedTones] = useState<string[]>(draft.selectedTones)
  const [toneText, setToneText] = useState(draft.toneText)
  const [characterText, setCharacterText] = useState(draft.characterText)
  const [selectedPerspective, setSelectedPerspective] = useState<string>(draft.selectedPerspective)
  const [perspectiveText, setPerspectiveText] = useState(draft.perspectiveText)
  const [selectedSetting, setSelectedSetting] = useState<string>(draft.selectedSetting)
  const [settingText, setSettingText] = useState(draft.settingText)
  const [promptText, setPromptText] = useState(draft.promptText)

  useEffect(() => {
    const nextDraft: OnboardingDraft = {
      step,
      genreLabels: selectedGenreItems.map((item) => item.label),
      selectedTones,
      toneText,
      characterText,
      selectedPerspective,
      perspectiveText,
      selectedSetting,
      settingText,
      promptText
    }
    try {
      localStorage.setItem(getOnboardingStorageKey(storyId), JSON.stringify(nextDraft))
    } catch {
      // localStorage unavailable (private browsing, quota) - draft just won't persist
    }
  }, [
    storyId,
    step,
    selectedGenreItems,
    selectedTones,
    toneText,
    characterText,
    selectedPerspective,
    perspectiveText,
    selectedSetting,
    settingText,
    promptText
  ])

  const handleTogglePopularGenre = (genre: GenreItem) => {
    setSelectedGenreItems((prev) => {
      if (prev.some((item) => item.id === genre.id)) {
        return prev.filter((item) => item.id !== genre.id)
      }
      if (prev.length >= 3) {
        return prev
      }
      return [...prev, genre]
    })
  }

  const handleSelectToneButton = (tone: ToneInfo) => {
    setSelectedTones([tone.name])
    setToneText(tone.detailedDescription)
  }

  const handleSelectPerspectiveButton = (perspective: PerspectiveInfo) => {
    setSelectedPerspective(perspective.name)
    setPerspectiveText(perspective.detailedDescription)
  }

  const handleSelectSettingButton = (setting: SettingInfo) => {
    setSelectedSetting(setting.name)
    setSettingText(setting.detailedDescription)
  }

  const handleSkipToPromptFromGenre = () => {
    setSelectedGenreItems([])
    setSelectedTones([])
    setToneText('')
    setCharacterText('')
    setSelectedPerspective('')
    setPerspectiveText('')
    setSelectedSetting('')
    setSettingText('')
    setStep('prompt')
  }

  const handleSkipToPromptFromTone = () => {
    setSelectedTones([])
    setToneText('')
    setCharacterText('')
    setSelectedPerspective('')
    setPerspectiveText('')
    setSelectedSetting('')
    setSettingText('')
    setStep('prompt')
  }

  const handleSkipToPromptFromCharacter = () => {
    setCharacterText('')
    setSelectedPerspective('')
    setPerspectiveText('')
    setSelectedSetting('')
    setSettingText('')
    setStep('prompt')
  }

  const handleSkipToPromptFromPerspective = () => {
    setSelectedPerspective('')
    setPerspectiveText('')
    setSelectedSetting('')
    setSettingText('')
    setStep('prompt')
  }

  const handleSkipToPromptFromSetting = () => {
    setSelectedSetting('')
    setSettingText('')
    setStep('prompt')
  }

  const handleGoToTones = () => {
    setStep('tone')
  }

  const handleGoToCharacter = () => {
    setStep('character')
  }

  const handleGoToPerspective = () => {
    setStep('perspective')
  }

  const handleGoToSetting = () => {
    setStep('setting')
  }

  const handleGoToPrompt = () => {
    setStep('prompt')
  }

  const handlePromptSubmit = async () => {
    if (promptText.trim()) {
      // Only clear the saved draft once onComplete confirms success. If the wiki-generation
      // pipeline fails partway (network blip, server error), the draft survives so a remount
      // restores everything the writer entered instead of silently discarding it.
      const succeeded = await onComplete(
        selectedGenres,
        selectedTones,
        toneText,
        characterText,
        selectedPerspective,
        perspectiveText,
        selectedSetting,
        settingText,
        promptText
      )
      if (succeeded) {
        clearOnboardingDraft(storyId)
      }
    }
  }

  const handleBackStep = () => {
    if (step === 'genre') {
      onExit?.()
      return
    }
    if (step === 'tone') {
      setStep('genre')
    } else if (step === 'character') {
      setStep('tone')
    } else if (step === 'perspective') {
      setStep('character')
    } else if (step === 'setting') {
      setStep('perspective')
    } else if (step === 'prompt') {
      setStep('setting')
    }
  }

  return (
    <div {...stylex.props(styles.root)}>
      {step === 'genre' ? (
        <div {...stylex.props(styles.container)}>
          {/* Header Section */}
          <div {...stylex.props(styles.headerGroup)}>
            <h1 {...stylex.props(styles.heading)}>Pick a genre for your story</h1>
            <p {...stylex.props(styles.genreNoticeText)}>You can always change the genres later</p>
          </div>

          {/* Genre Tokenizer (single-line pill input, styled like the tone step) */}
          <div {...stylex.props(styles.buttonsContainer, styles.genreButtonsContainer)}>
            <Tokenizer
              label="Genres"
              isLabelHidden
              searchSource={GENRE_SEARCH_SOURCE}
              value={selectedGenreItems}
              onChange={(items) => setSelectedGenreItems(items)}
              renderToken={(item, onRemove) => (
                <Token
                  key={item.id}
                  label={item.label}
                  color={item.auxiliaryData?.color}
                  size="lg"
                  onRemove={onRemove}
                />
              )}
              renderItem={(item) => (
                <div {...stylex.props(styles.genreDropdownItem)}>
                  <Token label={item.label} color={item.auxiliaryData?.color} size="sm" />
                  {item.auxiliaryData?.description && (
                    <span {...stylex.props(styles.genreDropdownDescription)}>
                      {item.auxiliaryData.description}
                    </span>
                  )}
                </div>
              )}
              placeholder="Search genres…"
              hasEntriesOnFocus
              hasClear
              maxEntries={3}
              className="genre-tokenizer"
            />
            <p {...stylex.props(styles.genreCaption)}>
              Pick <em>up to</em> 3 genres
            </p>

            {/* Popular Genres quick-select chips */}
            <div {...stylex.props(styles.popularGenresRow)}>
              <span {...stylex.props(styles.popularGenresLabel)}>Popular Genres:</span>
              {POPULAR_GENRE_ITEMS.map((genre) => {
                const isSelected = selectedGenreItems.some((item) => item.id === genre.id)
                const isDisabled = !isSelected && selectedGenreItems.length >= 3
                return (
                  <Tooltip key={genre.id} content={genre.auxiliaryData?.description ?? ''}>
                    <button
                      type="button"
                      onClick={() => handleTogglePopularGenre(genre)}
                      disabled={isDisabled}
                      {...stylex.props(
                        styles.genreButton,
                        isSelected && styles.genreButtonSelected,
                        isDisabled && styles.genreButtonDisabled
                      )}
                    >
                      {genre.label}
                    </button>
                  </Tooltip>
                )
              })}
            </div>
          </div>

          {/* Action Button Section */}
          <div {...stylex.props(styles.actionContainer)}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              {onExit && (
                <button
                  type="button"
                  onClick={handleBackStep}
                  {...stylex.props(styles.backButton)}
                  aria-label="Go back"
                >
                  <ArrowLeft size={18} />
                </button>
              )}
              <button
                type="button"
                onClick={handleGoToTones}
                disabled={selectedGenres.length === 0}
                {...stylex.props(
                  styles.forwardButton,
                  selectedGenres.length === 0 && styles.forwardButtonDisabled
                )}
                aria-label="Pick a tone"
              >
                <ArrowRight size={18} />
              </button>
            </div>
            <button
              type="button"
              onClick={handleSkipToPromptFromGenre}
              {...stylex.props(styles.skipLink)}
            >
              Skip to prompt
            </button>
          </div>
        </div>
      ) : step === 'tone' ? (
        <div {...stylex.props(styles.container)}>
          {/* Header Section */}
          <h1 {...stylex.props(styles.heading)}>Pick a tone for your story</h1>

          {/* Custom Styled Textarea Box (No Send Button) */}
          <div {...stylex.props(styles.toneTextareaContainer)}>
            <textarea
              value={toneText}
              onChange={(e) => setToneText(e.target.value)}
              placeholder="Selected tone explanation will appear here, or write your own custom tone..."
              {...stylex.props(styles.toneTextarea)}
            />
          </div>

          {/* Buttons Section (5 per line, natural label width) */}
          <div {...stylex.props(styles.buttonsContainer)}>
            {[TONES.slice(0, 5), TONES.slice(5, 10), TONES.slice(10, 15)].map((row, rowIndex) => (
              <div key={rowIndex} {...stylex.props(styles.row)}>
                {row.map((tone) => {
                  const isSelected = selectedTones.includes(tone.name)
                  return (
                    <Tooltip key={tone.name} content={tone.description}>
                      <button
                        type="button"
                        onClick={() => handleSelectToneButton(tone)}
                        {...stylex.props(
                          styles.genreButton,
                          isSelected && styles.genreButtonSelected
                        )}
                      >
                        {tone.name}
                      </button>
                    </Tooltip>
                  )
                })}
              </div>
            ))}
          </div>

          {/* Action Button Section */}
          <div {...stylex.props(styles.actionContainer)}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <button
                type="button"
                onClick={handleBackStep}
                {...stylex.props(styles.backButton)}
                aria-label="Go back"
              >
                <ArrowLeft size={18} />
              </button>
              <button
                type="button"
                onClick={handleGoToCharacter}
                disabled={selectedTones.length === 0 && toneText.trim() === ''}
                {...stylex.props(
                  styles.forwardButton,
                  selectedTones.length === 0 &&
                    toneText.trim() === '' &&
                    styles.forwardButtonDisabled
                )}
                aria-label="Next step"
              >
                <ArrowRight size={18} />
              </button>
            </div>
            <button
              type="button"
              onClick={handleSkipToPromptFromTone}
              {...stylex.props(styles.skipLink)}
            >
              Skip to prompt
            </button>
          </div>
        </div>
      ) : step === 'character' ? (
        <div {...stylex.props(styles.container)}>
          {/* Header Section */}
          <h1 {...stylex.props(styles.heading)}>Describe your main characters</h1>

          {/* Character Description Custom Styled Textarea Box */}
          <div {...stylex.props(styles.characterTextareaContainer)}>
            <textarea
              value={characterText}
              onChange={(e) => setCharacterText(e.target.value)}
              placeholder="e.g. John Doe, 32, a weary detective seeking redemption..."
              {...stylex.props(styles.characterTextarea)}
            />
          </div>

          {/* Characters guide text block */}
          <p {...stylex.props(styles.characterGuideText)}>
            Describe the main characters: their names, age, gender. What they&apos;re like, what
            drives them, where they are in life, etc. Anything that would add to the story.
          </p>

          {/* Action Button Section */}
          <div {...stylex.props(styles.actionContainer)}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <button
                type="button"
                onClick={handleBackStep}
                {...stylex.props(styles.backButton)}
                aria-label="Go back"
              >
                <ArrowLeft size={18} />
              </button>
              <button
                type="button"
                onClick={handleGoToPerspective}
                disabled={characterText.trim() === ''}
                {...stylex.props(
                  styles.forwardButton,
                  characterText.trim() === '' && styles.forwardButtonDisabled
                )}
                aria-label="Next step"
              >
                <ArrowRight size={18} />
              </button>
            </div>
            <button
              type="button"
              onClick={handleSkipToPromptFromCharacter}
              {...stylex.props(styles.skipLink)}
            >
              Skip to prompt
            </button>
          </div>
        </div>
      ) : step === 'perspective' ? (
        <div {...stylex.props(styles.container)}>
          {/* Header Section */}
          <h1 {...stylex.props(styles.heading)}>Choose a narrative perspective for your story</h1>

          {/* Perspective Explanation Custom Textarea Box */}
          <div {...stylex.props(styles.toneTextareaContainer)}>
            <textarea
              value={perspectiveText}
              onChange={(e) => setPerspectiveText(e.target.value)}
              placeholder="Selected perspective description will appear here, or write your own custom perspective..."
              {...stylex.props(styles.toneTextarea)}
            />
          </div>

          {/* Buttons Section (Max 5, natural width) */}
          <div {...stylex.props(styles.buttonsContainer)}>
            <div {...stylex.props(styles.row)}>
              {PERSPECTIVES.map((p) => {
                const isSelected = selectedPerspective === p.name
                return (
                  <Tooltip key={p.name} content={p.description}>
                    <button
                      type="button"
                      onClick={() => handleSelectPerspectiveButton(p)}
                      {...stylex.props(
                        styles.genreButton,
                        isSelected && styles.genreButtonSelected
                      )}
                    >
                      {p.name}
                    </button>
                  </Tooltip>
                )
              })}
            </div>
          </div>

          {/* Action Button Section */}
          <div {...stylex.props(styles.actionContainer)}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <button
                type="button"
                onClick={handleBackStep}
                {...stylex.props(styles.backButton)}
                aria-label="Go back"
              >
                <ArrowLeft size={18} />
              </button>
              <button
                type="button"
                onClick={handleGoToSetting}
                disabled={selectedPerspective === '' && perspectiveText.trim() === ''}
                {...stylex.props(
                  styles.forwardButton,
                  selectedPerspective === '' &&
                    perspectiveText.trim() === '' &&
                    styles.forwardButtonDisabled
                )}
                aria-label="Next step"
              >
                <ArrowRight size={18} />
              </button>
            </div>
            <button
              type="button"
              onClick={handleSkipToPromptFromPerspective}
              {...stylex.props(styles.skipLink)}
            >
              Skip to prompt
            </button>
          </div>
        </div>
      ) : step === 'setting' ? (
        <div {...stylex.props(styles.container)}>
          {/* Header Section */}
          <h1 {...stylex.props(styles.heading)}>Choose a scene and setting for your story</h1>

          {/* Setting Explanation Custom Textarea Box */}
          <div {...stylex.props(styles.characterTextareaContainer)}>
            <textarea
              value={settingText}
              onChange={(e) => setSettingText(e.target.value)}
              placeholder="Selected scene and setting description will appear here, or write your own custom setting..."
              {...stylex.props(styles.characterTextarea)}
            />
          </div>

          {/* Buttons Section (Max 5, natural width) */}
          <div {...stylex.props(styles.buttonsContainer)}>
            <div {...stylex.props(styles.row)}>
              {SETTINGS.map((s) => {
                const isSelected = selectedSetting === s.name
                return (
                  <Tooltip key={s.name} content={s.description}>
                    <button
                      type="button"
                      onClick={() => handleSelectSettingButton(s)}
                      {...stylex.props(
                        styles.genreButton,
                        isSelected && styles.genreButtonSelected
                      )}
                    >
                      {s.name}
                    </button>
                  </Tooltip>
                )
              })}
            </div>
          </div>

          {/* Action Button Section */}
          <div {...stylex.props(styles.actionContainer)}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <button
                type="button"
                onClick={handleBackStep}
                {...stylex.props(styles.backButton)}
                aria-label="Go back"
              >
                <ArrowLeft size={18} />
              </button>
              <button
                type="button"
                onClick={handleGoToPrompt}
                disabled={selectedSetting === '' && settingText.trim() === ''}
                {...stylex.props(
                  styles.forwardButton,
                  selectedSetting === '' &&
                    settingText.trim() === '' &&
                    styles.forwardButtonDisabled
                )}
                aria-label="Next step"
              >
                <ArrowRight size={18} />
              </button>
            </div>
            <button
              type="button"
              onClick={handleSkipToPromptFromSetting}
              {...stylex.props(styles.skipLink)}
            >
              Skip to prompt
            </button>
          </div>
        </div>
      ) : (
        <div {...stylex.props(styles.container)}>
          {/* Header Section */}
          <h1 {...stylex.props(styles.heading)}>Where does your story begin?</h1>

          {/* Outline/First Scene Custom Textarea Box */}
          <div {...stylex.props(styles.characterTextareaContainer)}>
            <textarea
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              placeholder="e.g. A young archer discovers a hidden valley where time stands still, but she can only stay for nine nights..."
              {...stylex.props(styles.characterTextarea)}
            />
          </div>

          {/* Prompt guide text block */}
          <p {...stylex.props(styles.characterGuideText)}>
            Describe your story&apos;s outline, map out key plot points, or draft the opening scene.
            Your co-writer will use this to kickstart your first writing session.
          </p>

          {/* Action Button Section */}
          <div {...stylex.props(styles.actionContainer)}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <button
                type="button"
                onClick={handleBackStep}
                {...stylex.props(styles.backButton)}
                aria-label="Go back"
              >
                <ArrowLeft size={18} />
              </button>
              <button
                type="button"
                onClick={handlePromptSubmit}
                disabled={promptText.trim() === ''}
                {...stylex.props(
                  styles.forwardButton,
                  promptText.trim() === '' && styles.forwardButtonDisabled
                )}
                aria-label="Submit onboarding details"
              >
                <ArrowRight size={18} />
              </button>
            </div>
            {/* Bypass to Editor Link */}
            <button
              type="button"
              onClick={async () => {
                const succeeded = await onComplete(
                  selectedGenres,
                  selectedTones,
                  toneText,
                  characterText,
                  selectedPerspective,
                  perspectiveText,
                  selectedSetting,
                  settingText,
                  ''
                )
                if (succeeded) {
                  clearOnboardingDraft(storyId)
                }
              }}
              {...stylex.props(styles.scratchLink)}
            >
              Start writing from scratch
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
